/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Linkedin } from 'lucide-react';
import { LogoMark } from './LogoMark';
import { getComparerHref } from '../lib/compareAnchor';
import { identityFooterParts } from '../lib/orgIdentity';
import { SURFACES, trackEvent } from '../lib/gtag';
import { providerHubPath } from '../lib/providerRoutes.js';

const LINKEDIN_URL = 'https://linkedin.com/company/ere-vergelijk';

export const Footer: React.FC = () => {
  const { language, t } = useLanguage();
  const year = new Date().getFullYear();
  // Who publishes this site — legal name, KvK number, registered office.
  // `null` whenever lib/orgIdentity.ts holds no publishable identity: the
  // block is then absent entirely rather than showing a half row or
  // placeholder dashes, which would read worse than saying nothing. Filled in
  // since the VOF registration.
  const identity = identityFooterParts();
  // Where the comparison starts (Stap 1 at the top from `sm`, the results on a
  // phone) with the audience preset; see getComparerHref.
  const compareParticulierHref = getComparerHref(language, 'Particulier');

  // Same param key as the header's nav_click (`nav_item`, was `link_name`),
  // so "which nav links get clicked site-wide" is one query on one key.
  const trackFooterNav = (linkName: string, path: string) =>
    trackEvent('footer_nav_click', {
      nav_item: linkName,
      path,
      surface: SURFACES.footer,
      language,
    });

  // id: the floating compare CTA (FloatingCompareCta.tsx) observes this
  // element and steps aside while any of it is in the viewport.
  return (
    <footer id="site-footer" className="bg-slate-900 text-slate-300 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-12">
          <div className="col-span-1 md:col-span-2">
            <Link
              to={`/${language}`}
              onClick={() => trackEvent('logo_click', { surface: SURFACES.footer, language })}
              className="flex items-center gap-2 mb-6"
            >
              <LogoMark className="w-10 h-10 text-emerald-500" title="ERE-Vergelijk" />
              <span className="text-2xl font-black tracking-tight text-white">
                ERE-<span className="text-emerald-500">Vergelijk</span>
              </span>
            </Link>
            <p className="text-base text-slate-400 max-w-xs leading-relaxed">
              {t('footer.description')}
            </p>
            {/* Outbound tracking: covered by the global initOutboundClickTracking
                listener (aria-label doubles as the tracked link_text). */}
            <a
              href={LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
              className="mt-6 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-800 text-slate-400 hover:text-emerald-500 hover:border-emerald-500/50 transition-colors"
            >
              <Linkedin className="w-5 h-5" />
            </a>
          </div>

          {/* VERGELIJKEN */}
          <div>
            <h3 className="text-white font-black uppercase text-xs tracking-widest mb-6">
              {t('nav.compare')}
            </h3>
            <ul className="space-y-4 text-sm font-medium">
              <li>
                <Link
                  to={compareParticulierHref}
                  onClick={() => trackFooterNav('compare_particulier', compareParticulierHref)}
                  className="hover:text-emerald-500 transition-colors"
                >
                  {t('filters.particulier')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${language}/zakelijk`}
                  onClick={() => trackFooterNav('zakelijk', `/${language}/zakelijk`)}
                  className="hover:text-emerald-500 transition-colors"
                >
                  {t('nav.business')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${language}/mid-check`}
                  onClick={() => trackFooterNav('mid_check', `/${language}/mid-check`)}
                  className="hover:text-emerald-500 transition-colors"
                >
                  {t('nav.mid_check')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${language}/laadpalen`}
                  onClick={() => trackFooterNav('charger_brands', `/${language}/laadpalen`)}
                  className="hover:text-emerald-500 transition-colors"
                >
                  {t('nav.charger_brands')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${language}/geen-laadpaal`}
                  onClick={() => trackFooterNav('geen_laadpaal', `/${language}/geen-laadpaal`)}
                  className="hover:text-emerald-500 transition-colors"
                >
                  {t('nav.no_charger_v2')}
                </Link>
              </li>
            </ul>
          </div>

          {/* AANBIEDERS */}
          <div>
            <h3 className="text-white font-black uppercase text-xs tracking-widest mb-6">
              {t('footer.providers')}
            </h3>
            <ul className="space-y-4 text-sm font-medium">
              <li>
                <Link
                  to={providerHubPath(language)}
                  onClick={() => trackFooterNav('nea_providers', providerHubPath(language))}
                  className="hover:text-emerald-500 transition-colors"
                >
                  {t('footer.nea_providers')}
                </Link>
              </li>
              {/* The provider list and how we rank it belong together. */}
              <li>
                <Link
                  to={`/${language}/hoe-wij-vergelijken`}
                  onClick={() =>
                    trackFooterNav('how_we_compare', `/${language}/hoe-wij-vergelijken`)
                  }
                  className="hover:text-emerald-500 transition-colors"
                >
                  {t('nav.how_we_compare')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${language}/partner`}
                  onClick={() => trackFooterNav('partner', `/${language}/partner`)}
                  className="hover:text-emerald-500 transition-colors"
                >
                  {t('footer.partner')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${language}/partner-login`}
                  onClick={() => trackFooterNav('partner_portal', `/${language}/partner-login`)}
                  className="hover:text-emerald-500 transition-colors"
                >
                  {t('footer.partner_portal')}
                </Link>
              </li>
            </ul>
          </div>

          {/* ONTDEKKEN */}
          <div>
            <h3 className="text-white font-black uppercase text-xs tracking-widest mb-6">
              {t('footer.information')}
            </h3>
            <ul className="space-y-4 text-sm font-medium">
              <li>
                <Link
                  to={`/${language}/about`}
                  onClick={() => trackFooterNav('about', `/${language}/about`)}
                  className="hover:text-emerald-500 transition-colors"
                >
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${language}/kennisbank`}
                  onClick={() => trackFooterNav('kennisbank', `/${language}/kennisbank`)}
                  className="hover:text-emerald-500 transition-colors"
                >
                  {t('nav.faq')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${language}/begrippen`}
                  onClick={() => trackFooterNav('begrippen', `/${language}/begrippen`)}
                  className="hover:text-emerald-500 transition-colors"
                >
                  {t('nav.glossary')}
                </Link>
              </li>
              {/* The price index sits with the reference material, not with
                  the providers: it is a dataset we maintain, like the
                  glossary and the knowledge base. */}
              <li>
                <Link
                  to={`/${language}/ere-prijs`}
                  onClick={() => trackFooterNav('ere_price', `/${language}/ere-prijs`)}
                  className="hover:text-emerald-500 transition-colors"
                >
                  {t('nav.ere_price')}
                </Link>
              </li>
              <li>
                <Link
                  to={`/${language}/contact`}
                  onClick={() => trackFooterNav('contact', `/${language}/contact`)}
                  className="hover:text-emerald-500 transition-colors"
                >
                  {t('nav.contact')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-fg-muted font-medium">
          <div className="text-center md:text-left">
            <p>
              &copy; {year} ERE-Vergelijk.nl. {t('footer.rights')}
            </p>
            {/* <address> (un-italicised), not a <p>: this IS the publisher's
                contact/identity information, and the element is what makes it
                machine-readable as such. Separators are aria-hidden so a
                screen reader gets three facts, not "middle dot". */}
            {identity && (
              <address className="not-italic mt-1.5">
                <span className="sr-only">{t('footer.identity_label')}: </span>
                {identity.legalName}
                <span aria-hidden="true"> · </span>
                {t('footer.identity_kvk_label')} {identity.kvk}
                {identity.city && (
                  <>
                    <span aria-hidden="true"> · </span>
                    {identity.city}
                  </>
                )}
              </address>
            )}
          </div>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 sm:gap-x-6">
            <Link
              to={`/${language}/privacy`}
              onClick={() => trackFooterNav('privacy', `/${language}/privacy`)}
              className="hover:text-slate-300 transition-colors"
            >
              {t('footer.privacy')}
            </Link>
            <Link
              to={`/${language}/terms`}
              onClick={() => trackFooterNav('terms', `/${language}/terms`)}
              className="hover:text-slate-300 transition-colors"
            >
              {t('footer.terms')}
            </Link>
            <Link
              to={`/${language}/cookies`}
              onClick={() => trackFooterNav('cookies', `/${language}/cookies`)}
              className="hover:text-slate-300 transition-colors"
            >
              {t('footer.cookies')}
            </Link>
            <Link
              to={`/${language}/disclaimer`}
              onClick={() => trackFooterNav('disclaimer', `/${language}/disclaimer`)}
              className="hover:text-slate-300 transition-colors"
            >
              {t('footer.disclaimer')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
